import React from 'react';
import { Code, Cloud, Settings, Users, ArrowRight, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { companyData } from '../data/content';

const Services = () => {
  const iconMap = {
    Code: Code,
    Cloud: Cloud,
    Settings: Settings,
    Users: Users
  };

  const additionalServices = [
    'Web Application Development',
    'Desktop Application Development', 
    'Mobile Application Development',
    'Sage 50 Accounting (Pastel Partner)',
    'Sage 200 Evolution Consultation',
    'VIP Premier Payroll Systems',
    'Sage 300 People Payroll',
    'IT Business Support'
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.section 
      id="services" 
      className="py-20 bg-white dark:bg-gray-900"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
    >
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-16"
          variants={itemVariants}
        >
          <motion.h2 
            className="text-4xl font-bold text-gray-900 dark:text-white mb-6"
            whileHover={{ scale: 1.02 }}
          >
            Our Services
          </motion.h2>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Comprehensive Business Software Consultancy solutions tailored to your needs
          </p>
        </motion.div>

        {/* Main Services Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {companyData.services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <div 
                key={index}
                className="group bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-coral-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-primary-500 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                      {service.description}
                    </p>
                    
                    <button className="inline-flex items-center text-primary-500 font-semibold hover:text-primary-600 transition-colors duration-300 group-hover:translate-x-2">
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Services */}
        <div className="bg-gradient-to-br from-gray-50 to-primary-50 dark:from-gray-800 dark:to-primary-900/30 rounded-3xl p-8 lg:p-12">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Additional Expertise
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-lg">
              Specialized solutions across various platforms and technologies
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalServices.map((service, index) => (
              <div 
                key={index}
                className="flex items-center space-x-3 bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <CheckCircle className="w-5 h-5 text-primary-500 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300 font-medium text-sm">
                  {service}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-primary-500 to-coral-500 rounded-2xl p-8 lg:p-12 text-white">
            <h3 className="text-3xl font-bold mb-4">
              Ready to Transform Your Business?
            </h3>
            <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
              Let's discuss how our software consultancy services can help your business grow and succeed in the digital age.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => {
                  const element = document.querySelector('#contact');
                  if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="px-8 py-4 bg-white text-primary-500 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300 transform hover:scale-105"
              >
                Get Free Consultation
              </button>
              
              <button 
                onClick={() => {
                  const element = document.querySelector('#about');
                  if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-all duration-300"
              >
                Learn About Us
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default Services;